import type { RoutineProcedureId } from "@app/lib/api/routines/procedures";

export const routineNeeds = [
  "read_mail",
  "read_calendar",
  "read_notes",
  "read_wiki",
  "write_wiki",
  "create_task",
  "file_in_tracker",
  "read_tracker",
  "post_message",
  "read_chat",
  "search_web",
] as const;

export type RoutineNeed = (typeof routineNeeds)[number];

export type RoutineStepRole =
  | "source"
  | "context"
  | "transform"
  | "sink"
  | "notify";

/** One line of a recipe. A need names a capability, never a product. */
export interface RoutineStep {
  role: RoutineStepRole;
  label: string;
  need?: RoutineNeed;
  optional?: true;
  /** The procedure that carries out this step, when it has one of its own. */
  skill?: RoutineProcedureId;
}

/**
 * Servers known to satisfy each need, by scope name — internal server names as
 * `constants.ts` spells them, remote ones as `remote_servers.ts` names them. A
 * name here that this workspace has never installed is simply never connected
 * and never offered. Friday may also propose a configured server that is not
 * listed here; this list is the default suggestion, not a fence.
 */
export const capabilityServers: Record<RoutineNeed, readonly string[]> = {
  read_mail: ["gmail"],
  read_calendar: ["google_calendar"],
  read_notes: ["Granola", "Fireflies", "fathom"],
  read_wiki: ["user_wiki"],
  write_wiki: ["user_wiki"],
  create_task: ["meeting_tasks", "Linear", "jira", "ClickUp"],
  // Where the team tracks work, never Home's proposal list: a change people
  // approved in a review becomes an issue in their tracker.
  file_in_tracker: ["ClickUp", "Linear", "jira"],
  read_tracker: ["Linear", "jira", "ClickUp"],
  post_message: ["slack", "google_chat", "gmail"],
  read_chat: ["slack", "google_chat"],
  search_web: ["web_search_&_browse"],
};

/**
 * One step against what this workspace has: which servers could satisfy it,
 * which one it runs on, and which are known but not connected. The single
 * resolution rule — the wire's step list, the enable guard and the agent's own
 * tool scope all read it here rather than each re-deriving it.
 */
export function resolveStepServers(
  step: RoutineStep,
  {
    configured,
    resolvedTools,
  }: { configured: Set<string>; resolvedTools: Record<string, string> }
): { resolved: string | null; options: string[]; available: string[] } {
  if (!step.need) {
    return { resolved: null, options: [], available: [] };
  }

  const known = capabilityServers[step.need];
  const options = known.filter((name) => configured.has(name));

  return {
    resolved: resolvedTools[step.label] ?? options[0] ?? null,
    options,
    available: known.filter((name) => !configured.has(name)),
  };
}
